import React from "react";
import * as m from "../../lib/model";
import { qsbsBump } from "./Qsbs";
import { fmtBig, entryMult } from "./deck";
import { plCagr } from "../FinancialBlocks";

const pct = (n) => (isNaN(n) ? "n/a" : m.fmtPct(n));

function Strip({ items, testid }) {
  return (
    <div className="ir-metric-strip" data-testid={testid}>
      {items.map(([k, val, sub]) => <div className="ir-metric" key={k}><span>{k}</span><b className="mono">{val}</b><em>{sub}</em></div>)}
    </div>
  );
}

export function FinancialsHead({ M }) {
  const hy = (M.historicalYears || []).filter((h) => h.revenue > 0);
  const first = hy[0], last = hy[hy.length - 1];
  const yrs = hy.length > 1 ? last.year - first.year : 0;
  const cagr = yrs > 0 && first.revenue > 0 ? (Math.pow(last.revenue / first.revenue, 1 / yrs) - 1) * 100 : null;
  const pf = m.proForma(M);
  const cov = m.coverage(M);
  const gmPct = m.grossMargin(M);
  const rows = m.model5yr(M);
  const covFcf = rows[cov.year - 1] || rows[0];
  const projCagr = plCagr(M);
  const summary = [
    `${fmtBig(pf.revenue)} revenue`,
    `${fmtBig(pf.sde)} adjusted earnings (${(pf.revenue ? (pf.sde / pf.revenue) * 100 : 0).toFixed(1)}% margin)`,
    `${m.fmtX(cov.dscr)} debt-service cover in year ${cov.year}`,
  ].join(" · ");
  return (
    <div data-testid="ir-fin-head">
      <div className="ir-deck-head" style={{ marginTop: 0 }}>
        <span className="ir-eyebrow">Financials{pf.pf ? " · pro-forma consolidated" : ""}</span>
        <p className="ir-deck-sum" data-testid="ir-fin-headline">{summary}</p>
      </div>
      <Strip testid="ir-fin-strip" items={[
        [pf.pf ? "Pro-forma revenue" : "TTM revenue", fmtBig(pf.revenue), pf.pf ? `all ${pf.count} add-ons closed · before growth` : last ? `FY${last.year}` : "trailing 12 months"],
        [pf.pf ? "Pro-forma adj. SDE" : "Adjusted SDE", fmtBig(pf.sde), pf.pf ? `platform ${fmtBig(M.sde)} + ${pf.count} add-on${pf.count > 1 ? "s" : ""}` : `${(pf.revenue ? (pf.sde / pf.revenue) * 100 : 0).toFixed(1)}% margin`],
        ["Gross margin", gmPct ? m.fmtPct(gmPct) : "—", gmPct ? (pf.pf ? "platform reported" : "reported") : "not disclosed"],
        ["Revenue CAGR", cagr != null ? pct(cagr) : "—", cagr != null ? `${first.year}–${last.year}` : "single year"],
        [`Revenue CAGR (Y1–Y${projCagr.years})`, projCagr.pct ? pct(projCagr.pct) : "—", `${M.sdeGrowth}% growth assumption`],
        ["Entry multiple", m.fmtX(entryMult(M)), m.isRollupScope(M) ? "consolidated · price ÷ EBITDA" : "on adjusted SDE"],
        [`Year-${cov.year} free cash`, fmtBig(covFcf?.fcf || 0), cov.pf ? "fully deployed · after debt service" : "after debt service"],
      ]} />
    </div>
  );
}

export function ProjectionCard({ M }) {
  const hold = Math.min(7, m.effectiveHold(M));
  const rows = m.model5yr(M).slice(0, hold);
  const pf = m.proForma(M);
  const pl = m.projectedPL(M);
  return (
    <div className="card table-scroll ir-gap" data-testid="ir-projection">
      <div className="card-title"><span>Projected earnings &amp; cash flow</span><span className="om-sub">{M.sdeGrowth}% growth · base case{pf.pf ? " · consolidated, add-ons phase in as they close" : ""}</span></div>
      <table className="tbl">
        <thead><tr><th>Year</th>{rows.map((r) => <th className="num" key={r.y}>Yr {r.y}</th>)}</tr></thead>
        <tbody>
          <tr><td>Revenue{pf.pf ? " (pro-forma)" : ""}</td>{rows.map((r) => <td className="num" key={r.y}>{m.fmt$((pl[r.y - 1] || {}).revenue || 0)}</td>)}</tr>
          <tr><td>Adjusted SDE / EBITDA</td>{rows.map((r) => <td className="num" key={r.y}>{m.fmt$(r.sde_y)}</td>)}</tr>
          <tr><td>Cash for debt service</td>{rows.map((r) => <td className="num" key={r.y}>{m.fmt$(r.cfads)}</td>)}</tr>
          <tr><td>Debt service</td>{rows.map((r) => <td className="num exp" key={r.y}>({m.fmt$(r.ds_y)})</td>)}</tr>
          <tr className="hl"><td><b>Free cash flow</b></td>{rows.map((r) => <td className={`num ${r.fcf >= 0 ? "pos" : "neg"}`} key={r.y}><b>{m.fmt$(r.fcf)}</b></td>)}</tr>
          <tr><td>DSCR</td>{rows.map((r) => <td className="num" key={r.y}>{m.fmtX(r.dscr)}</td>)}</tr>
        </tbody>
      </table>
    </div>
  );
}

export function ReturnsHead({ M, wf, ex }) {
  const bump = qsbsBump(M);
  const hold = M.holdIndefinite ? "an indefinite hold" : `${M.hold} years`;
  const interim = wf.rows.slice(0, -1).reduce((a, r) => a + r.lpYear, 0);
  const summary = [
    `${fmtBig(m.lpAskOf(M))} LP equity`,
    `${m.fmtX(wf.lpMoic)} / ${pct(wf.lpIrr)} IRR over ${hold}`,
    `${fmtBig(interim)} distributed before exit`,
    ...(M.holdIndefinite ? [] : [`${fmtBig(ex.equityAtExit)} at a ${m.fmtX(M.exitMult)} exit`]),
  ].join(" · ");
  return (
    <div data-testid="ir-returns-head">
      <div className="ir-deck-head" style={{ marginTop: 0 }}>
        <span className="ir-eyebrow">Returns</span>
        <p className="ir-deck-sum" data-testid="ir-returns-headline">{summary}</p>
      </div>
      <Strip testid="ir-returns-strip" items={[
        ["LP equity ask", fmtBig(m.lpAskOf(M)), "at close"],
        ["LP distributions", fmtBig(wf.lpTotal), bump ? `${m.fmtX(wf.lpMoic)} MOIC · QSBS +${m.fmtX(bump.x)}` : `${m.fmtX(wf.lpMoic)} MOIC`],
        ["LP IRR", pct(wf.lpIrr), bump && bump.bps > 0 ? `QSBS +${bump.bps} bps after tax` : "base case"],
        ["Preferred paid", fmtBig(wf.prefTotal), `${M.prefReturn}% cumulative`],
        ["Cash before exit", fmtBig(interim), `years 1–${Math.max(1, wf.rows.length - 1)}`],
        ["GP promote", fmtBig(wf.gpTotal), `${100 - (M.lpSplit == null ? 80 : M.lpSplit)}% after pref`],
      ]} />
    </div>
  );
}
